import dayjs from "dayjs";
import startCase from "lodash/startCase";
import {
  CASE_MATTER_STATUS_CATEGORY_OPTIONS,
  getMatterStatusCategoryLabel,
} from "@/lib/data/static/matterStatics";

export type ReportFilterSelectOption = {
  group?: string;
  value: string;
  label: string;
};

export const REPORT_DATE_RANGE_SEARCH_VALUES = [
  "ALL_TIME",
  "PREVIOUS_7_DAYS",
  "PREVIOUS_30_DAYS",
  "PREVIOUS_90_DAYS",
  "YEAR_TO_DATE",
  "CUSTOM_DATE_RANGE",
] as const;

export type ReportDateRangeType =
  (typeof REPORT_DATE_RANGE_SEARCH_VALUES)[number];

export const REPORT_DATE_RANGE_OPTIONS = [
  { value: "ALL_TIME", label: "All time" },
  { value: "PREVIOUS_7_DAYS", label: "Previous 7 Days" },
  { value: "PREVIOUS_30_DAYS", label: "Previous 30 Days" },
  { value: "PREVIOUS_90_DAYS", label: "Previous 90 Days" },
  { value: "YEAR_TO_DATE", label: "Year to date" },
  { value: "CUSTOM_DATE_RANGE", label: "Custom range" },
] as const satisfies ReadonlyArray<{
  label: string;
  value: ReportDateRangeType;
}>;

const REPORT_DATE_RANGE_VALUES = new Set<string>(
  REPORT_DATE_RANGE_SEARCH_VALUES,
);

export const isReportDateRangeType = (
  value: unknown,
): value is ReportDateRangeType =>
  typeof value === "string" && REPORT_DATE_RANGE_VALUES.has(value);

export const getReportDateRangeLabel = (value?: string) =>
  REPORT_DATE_RANGE_OPTIONS.find((option) => option.value === value)?.label ??
  value;

export const formatReportDateRangeBoundsLabel = (
  start?: string,
  end?: string,
) => {
  if (!start || !end) return undefined;
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  if (!startDate.isValid() || !endDate.isValid()) {
    return undefined;
  }

  const currentYear = dayjs().year();
  const includeYear =
    startDate.year() !== currentYear || endDate.year() !== currentYear;
  const pattern = includeYear ? "MMM D YYYY" : "MMM D";
  const startLabel = startDate.format(pattern);

  if (startDate.isSame(endDate, "day")) {
    return startLabel;
  }

  return `${startLabel} - ${endDate.format(pattern)}`;
};

export const resolveReportDateRange = ({
  dateRangeEnd,
  dateRangeStart,
  dateRangeType,
}: {
  dateRangeEnd?: string;
  dateRangeStart?: string;
  dateRangeType?: ReportDateRangeType;
}) => {
  if (!dateRangeType || dateRangeType === "ALL_TIME") {
    return {
      end: undefined,
      start: undefined,
    };
  }

  if (dateRangeType === "CUSTOM_DATE_RANGE") {
    const startDate = dayjs(dateRangeStart);
    const endDate = dayjs(dateRangeEnd);

    if (!startDate.isValid() || !endDate.isValid()) {
      return {
        end: undefined,
        start: undefined,
      };
    }

    return {
      end: endDate.format("YYYY-MM-DD"),
      start: startDate.format("YYYY-MM-DD"),
    };
  }

  const today = dayjs().startOf("day");
  const start =
    dateRangeType === "YEAR_TO_DATE"
      ? today.startOf("year")
      : today.subtract(
          dateRangeType === "PREVIOUS_7_DAYS"
            ? 7
            : dateRangeType === "PREVIOUS_30_DAYS"
              ? 30
              : 90,
          "day",
        );

  return {
    end: today.format("YYYY-MM-DD"),
    start: start.format("YYYY-MM-DD"),
  };
};

export const formatResolvedReportDateRangeLabel = ({
  dateRangeEnd,
  dateRangeStart,
  dateRangeType,
}: {
  dateRangeEnd?: string;
  dateRangeStart?: string;
  dateRangeType?: ReportDateRangeType;
}) => {
  if (!dateRangeType || dateRangeType === "ALL_TIME") {
    return undefined;
  }

  if (dateRangeType === "CUSTOM_DATE_RANGE") {
    return (
      formatReportDateRangeBoundsLabel(dateRangeStart, dateRangeEnd) ??
      getReportDateRangeLabel(dateRangeType)
    );
  }

  return getReportDateRangeLabel(dateRangeType);
};

export const buildMatterStatusValue = (
  label?: string | null,
  category?: string | null,
) => {
  const trimmedCategory = category?.trim() ?? "";
  const trimmedLabel = label?.trim() ?? "";
  return `${trimmedCategory}::${trimmedLabel}`;
};

export const parseMatterStatusValue = (value: string) => {
  const [category, ...labelParts] = value.split("::");
  if (labelParts.length === 0) {
    const label = category.trim();
    return {
      category: undefined,
      label: label || undefined,
    };
  }

  const label = labelParts.join("::");
  return {
    category: category.trim() || undefined,
    label: label.trim() || undefined,
  };
};

export const formatMatterStatusLabel = (label: string, category?: string) => {
  const trimmedLabel = label.trim();
  if (!category) {
    return trimmedLabel;
  }

  return `${trimmedLabel} (${startCase(category)})`;
};

export const buildMatterStatusValueOptions = (
  statuses:
    | Array<{
        category?: string | null;
        label?: string | null;
        categoryId?: string | null;
      }>
    | undefined,
  selectedValues: string[] = [],
): ReportFilterSelectOption[] => {
  const optionsMap = new Map<string, ReportFilterSelectOption>();

  statuses?.forEach((status) => {
    const label = status.label?.trim();
    if (!label) return;

    const category = status.category?.trim();
    const value = buildMatterStatusValue(label, category);
    if (optionsMap.has(value)) return;

    optionsMap.set(value, {
      value,
      label: formatMatterStatusLabel(label, category ?? undefined),
    });
  });

  selectedValues.forEach((value) => {
    if (optionsMap.has(value)) return;

    const { label, category } = parseMatterStatusValue(value);
    if (!label) return;

    optionsMap.set(value, {
      value,
      label: formatMatterStatusLabel(label, category),
    });
  });

  return Array.from(optionsMap.values());
};

export const buildMatterStatusLabelOptions = (
  statuses:
    | Array<{
        label?: string | null;
        categoryId?: string | null;
      }>
    | undefined,
  selectedValues: string[] = [],
): ReportFilterSelectOption[] => {
  const options = new Map<string, ReportFilterSelectOption>();

  statuses?.forEach((status) => {
    const label = status.label?.trim();
    if (
      label &&
      CASE_MATTER_STATUS_CATEGORY_OPTIONS.some(
        (category) => category === status.categoryId,
      )
    ) {
      options.set(label, {
        group: getMatterStatusCategoryLabel(status.categoryId),
        label,
        value: label,
      });
    }
  });

  selectedValues.forEach((value) => {
    const label = value.trim();
    if (label) {
      if (!options.has(label)) options.set(label, { label, value: label });
    }
  });

  return Array.from(options.values());
};
